<template>
    <div>
        <v-card class="mb-3 pa-3">
            <v-layout row align-center>
                <v-flex class="px-1">
                    <v-text-field hide-details single-line v-model.trim="newUser.email" label="Email"></v-text-field>
                </v-flex>
                <v-flex class="px-1">
                    <v-text-field hide-details single-line v-model.trim="newUser.name" label="Name"></v-text-field>
                </v-flex>
                <v-flex shrink class="px-1">
                    <v-chip
                        v-for="(site, index) in sites"
                        :key="index"
                        label
                        :disabled="user.site !== '' && user.site !== site"
                        @click="selectNewUserSite(site)"
                        :color="site === newUser.site ? 'info' : ''"
                    >
                        {{ site }}
                    </v-chip>
                </v-flex>
                <v-flex shrink class="px-1">
                    <v-checkbox v-model="newUser.supplement" hide-details label="Supplement"></v-checkbox>
                </v-flex>
                <v-flex shrink class="text-xs-right">
                    <v-btn :disabled="!canAddNewUser()" @click="addNewUser()">
                        <v-icon left>add</v-icon>
                        Add User
                    </v-btn>
                </v-flex>
            </v-layout>
        </v-card>
        <v-layout row align-center>
            <v-flex class="px-1">
                <v-btn @click="getUsers()">
                    <v-icon left>refresh</v-icon>
                    Refresh Data
                </v-btn>
            </v-flex>
            <v-flex shrink class="text-xs-right">
                <v-btn href="/api/admin/users?csv">
                    <v-icon left>download</v-icon>
                    Download
                </v-btn>
            </v-flex>
        </v-layout>
        <v-data-table :headers="headers" :items="users" :disable-initial-sort="true" :hide-actions="true" item-key="hex" class="elevation-1">
            <template v-slot:items="props">
                <tr :class="{ 'grey lighten-1': !props.item.enabled }">
                    <td class="no-wrap">
                        {{ props.item.email }}
                        <v-chip v-for="(role, index) in props.item.roles" :key="index" label small color="info" text-color="white">
                            {{ role }}
                        </v-chip>
                    </td>
                    <td>
                        <v-checkbox
                            v-model="props.item.enabled"
                            hide-details
                            @change="toggleEnabled(props.item)"
                            :disabled="props.item.email === user.email"
                        ></v-checkbox>
                    </td>
                    <td>{{ props.item.site }}</td>
                    <td>
                        <v-checkbox v-model="props.item.supplement" hide-details disabled></v-checkbox>
                    </td>
                    <td class="no-wrap">{{ props.item.name }}</td>
                    <td>
                        <span v-if="props.item.preferences">{{ props.item.preferences.pronoun }}</span>
                    </td>
                    <td>
                        <span v-if="props.item.auditCounts">{{ props.item.auditCounts.login || 0 }}</span>
                    </td>
                    <td>
                        <span v-if="props.item.auditCounts">{{ props.item.auditCounts.reset || 0 }}</span>
                    </td>
                    <td class="no-wrap">
                        <span v-if="props.item.createdDate">{{ props.item.createdDate | date }}</span>
                    </td>
                    <td class="no-wrap">
                        <span v-if="props.item.completedDate">{{ props.item.completedDate | date }}</span>
                    </td>
                    <td class="no-wrap">
                        <span v-if="props.item.lastActive">{{ props.item.lastActive | date }}</span>
                    </td>
                    <td>
                        <span v-if="props.item.lastActive">{{ props.item.lastActiveDays || 0 }}</span>
                    </td>
                    <td class="no-wrap">
                        <div v-if="props.item.sectionTiming">
                            <v-progress-linear :value="progress(props.item.sectionTiming)" class="mb-0"></v-progress-linear>
                            <div class="text-xs-center">{{ currentSection(props.item.sectionTiming) }} of {{ totalSections(props.item.sectionTiming) }}</div>
                        </div>
                    </td>
                    <td>{{ formatMs(props.item.sectionTiming[0]) }}</td>
                    <td>{{ formatMs(props.item.sectionTiming[1]) }}</td>
                    <td>{{ formatMs(props.item.sectionTiming[2]) }}</td>
                    <td>{{ formatMs(props.item.sectionTiming[3]) }}</td>
                    <td>{{ formatMs(props.item.sectionTiming[4]) }}</td>
                    <td>{{ formatMs(props.item.sectionTiming[5]) }}</td>
                    <td>{{ formatMs(props.item.sectionTiming[6]) }}</td>
                    <td>{{ formatMs(props.item.sectionTiming[7]) }}</td>
                    <td>{{ formatMs(props.item.sectionTiming[8]) }}</td>
                    <td>{{ formatMs(props.item.sectionTiming[9]) }}</td>
                    <td>{{ formatMs(props.item.sectionTiming[10]) }}</td>
                    <td>{{ formatMs(props.item.sectionTiming[11]) }}</td>
                    <td>{{ formatMs(props.item.sectionTiming[12]) }}</td>
                    <td>{{ formatMs(props.item.sectionTiming[13]) }}</td>
                    <td>{{ formatMs(props.item.sectionTiming[14]) }}</td>
                    <td>{{ formatMs(props.item.sectionTiming[15]) }}</td>
                    <td>{{ formatMs(props.item.sectionTiming[16]) }}</td>
                    <td>{{ formatMs(props.item.sectionTiming[17]) }}</td>
                    <td>{{ formatMs(props.item.sectionTiming[18]) }}</td>
                    <td>{{ formatMs(props.item.sectionTiming[19]) }}</td>
                    <td>{{ formatMs(props.item.sectionTiming[20]) }}</td>
                </tr>
            </template>
        </v-data-table>
    </div>
</template>

<script>
import api from '@/api';
import ms from 'ms';

export default {
    props: {
        user: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            sites: ['LCH', 'UCLA', 'CHLA'],
            newUser: {
                email: '',
                name: '',
                site: '',
                supplement: false
            },
            headers: [
                { text: 'Email', value: 'email' },
                { text: 'Enabled', value: 'enabled', sortable: false },
                { text: 'Site', value: 'site' },
                { text: 'Supplement', value: 'supplement' },
                { text: 'Name', value: 'name' },
                { text: 'Pronoun', value: 'pronoun', sortable: false },
                { text: 'Logins', value: 'logins', sortable: false },
                { text: 'PW Resets', value: 'resets', sortable: false },
                { text: 'Created', value: 'createdDate', sortable: false },
                { text: 'Completed', value: 'completedDate', sortable: false },
                { text: 'Last Active', value: 'lastActive', sortable: false },
                { text: 'Days Ago', value: 'lastActiveDays', sortable: false },
                { text: 'Progress', value: 'progress', sortable: false },
                { text: '1', value: '1', sortable: false },
                { text: '2', value: '2', sortable: false },
                { text: '3', value: '3', sortable: false },
                { text: '4', value: '4', sortable: false },
                { text: '5', value: '5', sortable: false },
                { text: '6', value: '6', sortable: false },
                { text: '7', value: '7', sortable: false },
                { text: '8', value: '8', sortable: false },
                { text: '9', value: '9', sortable: false },
                { text: '10', value: '10', sortable: false },
                { text: '11', value: '11', sortable: false },
                { text: '12', value: '12', sortable: false },
                { text: '13', value: '13', sortable: false },
                { text: '14', value: '14', sortable: false },
                { text: '15', value: '15', sortable: false },
                { text: '16', value: '16', sortable: false },
                { text: '17', value: '17', sortable: false },
                { text: '18', value: '18', sortable: false },
                { text: '19', value: '19', sortable: false },
                { text: '20', value: '20', sortable: false }
            ],
            users: []
        };
    },
    mounted() {
        this.resetNewUser();
        this.getUsers();
    },
    methods: {
        resetNewUser() {
            this.newUser.email = '';
            this.newUser.name = '';
            this.newUser.site = this.user.site || '';
            this.newUser.supplement = false;
        },
        selectNewUserSite(site) {
            if (this.user.site && this.user.site !== site) {
                return;
            }
            this.newUser.site = site;
        },
        canAddNewUser() {
            return this.newUser.email && this.newUser.name && this.newUser.site;
        },
        addNewUser() {
            if (this.canAddNewUser()) {
                api.post('/admin/register', this.newUser)
                    .then(() => {
                        this.getUsers();
                    })
                    .catch(error => {
                        alert(error.response.data);
                    })
                    .finally(() => {
                        this.resetNewUser();
                    });
            }
        },
        getUsers() {
            api.get('/admin/users').then(response => {
                this.users = response.data.users;
            });
        },
        toggleEnabled(user) {
            if (user.email !== this.user.email) {
                api.post('/admin/update', { email: user.email, enabled: user.enabled })
                    .then(() => {
                        this.getUsers();
                    })
                    .catch(error => {
                        alert(error.response.data);
                    });
            }
        },
        currentSection(sectionTiming) {
            let index = sectionTiming.indexOf(null);
            if (-1 === index) {
                return sectionTiming.length;
            }
            return index;
        },
        totalSections(sectionTiming) {
            return sectionTiming.length;
        },
        progress(sectionTiming) {
            return (this.currentSection(sectionTiming) / this.totalSections(sectionTiming)) * 100;
        },
        formatMs(value) {
            if (value) {
                return ms(value);
            }
            return null;
        }
    }
};
</script>
